import { defaultTo } from 'lodash';
import { SuggestKeywords } from 'core/product/Product';

/**
 * 出價上限與單一關鍵字建議出價區間 `[bidPrice, maxCpc]` 的關係。
 *
 * `優化目標` 的「出價試算」面板與 `基本資訊` 的「出價效益參考」卡片用的是同一組判定，
 * 只是標籤不同（競爭力強／大部分有量、可競爭／部分有量、低於建議／買不到量）⇒ 共用一份，
 * 不要各寫一次比較式。
 *
 * 邊界：
 * - 下限 inclusive（出價 = 建議下限 → COMPETITIVE）
 * - 上限 strict（出價 = 建議上限 → 仍是 COMPETITIVE；要嚴格大於才算 STRONG）
 *
 * `cpcCounts === 0`（後端的 CPC cache 裡沒有這個字，手輸入的冷門字大多如此）
 * 改用活動層級的預設區間 8–20 判定，見 `resolveKeywordBidRange`。
 * 這是 2026-09-02 的產品決定：讓每一列都算得出來、只有三種狀態，使用者才知道要設多少。
 *
 * 別把它「修回去」成不判定。已知的代價各記一筆在下面：
 * - `getKeywordCoverage` 的覆蓋率會趨近 100%（出價 ≥ 8 就幾乎全進分子）。
 * - 同畫面頂部的活動層級建議出價區間不含這些字（BE 算加權平均前 `.filter(Objects::nonNull)`
 *   把它們濾掉了）⇒ 「這個字建議 8–20」與「本活動建議 8–18」會並存。
 * 兩件都在 review 時攤開過並被接受，不是疏漏。
 */
export enum KeywordBidCompetitiveness {
  NOT_EVALUATED = 'notEvaluated',
  STRONG = 'strong',
  COMPETITIVE = 'competitive',
  BELOW_SUGGESTED = 'belowSuggested'
}

/**
 * 「完全沒有 CPC 資料」時的建議出價區間，語意與後端
 * `getSuggestedKeywordsCpcBidRange` 在 `cpcCounts == 0` 時回的 `defaultBidRange` 相同。
 *
 * 這對數字原本在 FE 散在兩處硬編碼（見 `KeywordFormModel.clampRecommendBidPrice`），
 * 改任一處不會有東西變紅 ⇒ 收斂成這一份，兩個消費端共用。
 * BE 那一份是 `PCHomeAPIService.DEFAULT_KEYWORD_BID_FLOOR_PRICE` / `..._CAP_PRICE`，
 * 跨 repo 沒有機械連結，改動時要兩邊一起改。
 */
export const DEFAULT_KEYWORD_BID_FLOOR = 8;
export const DEFAULT_KEYWORD_BID_CAP = 20;

/**
 * 這一列的 bidPrice / maxCpc 是不是真實資料。後端對不在 CPC cache 裡的字，兩個欄位都
 * orElse(20)，所以只有 cpcCounts 分得出來 —— 判別式是 cpcCounts > 0，不是「數值是不是 0」。
 *
 * 收斂成一份供 resolveKeywordBidRange 與 computeRecommendBidPrice 共用：兩邊各寫一次的話，
 * 一邊寫 `> 0`、另一邊寫 truthiness，看起來一樣但沒有東西保證它們會一起改。
 */
export const hasCpcData = (row: Pick<SuggestKeywords, 'cpcCounts'>): boolean =>
  defaultTo(row.cpcCounts, 0) > 0;

/**
 * 這一列該用哪個區間判定 / 顯示。四個區間顯示點與兩個狀態 capsule 共用這一份 ——
 * 各自寫一次 `cpcCounts ? … : …` 必漂：下次多一個顯示點就會漏。
 *
 * 判別式是 `cpcCounts > 0`，不是「數值是不是 0」：後端對沒有 CPC 資料的字，
 * `bidPrice` / `maxCpc` 兩個欄位都 `orElse(DEFAULT_KEYWORD_BID_CAP_PRICE)` ⇒ wire 上是
 * `[20, 20]` 這組 fallback 常數，看起來像真的。照它判定會得到「出價 25 > 20 ⇒ 競爭力強」
 * 這種毫無根據的斷言，而且它是單點不是區間（FLOOR 8 在 per-keyword 路徑從沒被用過，
 * 是 BE 既有的不對稱）⇒ 一律換成完整的預設區間。
 *
 * bidFloor = 經銷商出價設定的下限（order.campaignBidPrice[type].bidFloor[cpc|cpm]，由
 * RtbCampaignBasicFormModel.getBidPriceRange(optimize).min 取得）。三個設計決定：
 *
 * clamp 只套沒有 CPC 資料的列。判準是「兩頁是不是在講同一個量」：沒有 CPC 資料的列顯示的
 * 是預設區間，而 `基本資訊` 沒有成效列時顯示的也是它 ⇒ 同一個量、必須套同一份 clamp，否則
 * 兩頁對同一件事各說一套。有 CPC 資料的列顯示的是這個字的歷史行情，而 `基本資訊` 顯示的是
 * 活動層級加權平均 ⇒ 本來就是兩個不同的量，不存在「要對得起來」這回事（見本檔頂端「兩個
 * 來源，別混用」）。套到所有列會單向銷毀「這個字很便宜」這個資訊，還會讓判定失真 —— 5–9
 * 的字出價 15 本來是「競爭力強」，clamp 成 15–15 後變「可競爭」。
 *
 * 判別「要不要 clamp」不看來源（手動輸入 / 推薦），只看 cpcCounts。兩者不是同一個集合：
 * 推薦來的字也可能沒有 CPC 資料（在 KeywordInsightHistory 裡但不在 CPC cache 裡）。用來源
 * 判別會讓兩種「沒資料」的字顯示不一致，等於用新的不一致換掉舊的。
 *
 * bidFloor 是必填參數，不給預設值。預設 0 會讓漏傳的呼叫端靜默拿到未 clamp 的區間 —— 正是
 * 本檔開頭那句「各自寫一次必漂」要防的事。少傳就編譯不過。
 *
 * 不要為了省 Math.max 把 clamp 前移到 response 落地時。bidFloor 取自 getBidFloor(optimize)，
 * 會隨「優化目標」變 —— 前移之後切 CPC↔CPM 區間不會更新，而且不報錯。列數 ≤ 50，這裡沒有
 * 效能問題，前移換到的是一個 bug。
 */
export const resolveKeywordBidRange = (
  row: Pick<SuggestKeywords, 'bidPrice' | 'maxCpc' | 'cpcCounts'>,
  bidFloor: number
): { floor: number, cap: number } => {
  if (hasCpcData(row)) {
    // 有 CPC 資料 ⇒ 這一列講的是「這個字的歷史行情」,不是「你該出多少」。
    // 刻意不套經銷商下限：套了會抹掉「這個字其實很便宜」這個事實而使用者無從得知，
    // 判定也會失真 —— 一個 5–9 的便宜字，使用者出 15（他能出的最低價）本來是
    // 「競爭力強」，clamp 成 15–15 之後變「可競爭」，真實的相對關係被藏起來。
    return { floor: row.bidPrice, cap: row.maxCpc };
  }
  // 沒有 CPC 資料 ⇒ 用預設區間。而這個量與 `基本資訊` 顯示的是同一個 ——
  // `KeywordFormModel.clampRecommendBidPrice` 的 else 分支也是 `Math.max(8/20, bidFloor)`。
  // 兩邊必須套同一份 clamp，否則列表寫 8–20、基本資訊寫 15–20，對同一件事各說一套。
  // 改這裡就要一起改那裡，跨檔沒有機械連結。
  return {
    floor: Math.max(DEFAULT_KEYWORD_BID_FLOOR, bidFloor),
    cap: Math.max(DEFAULT_KEYWORD_BID_CAP, bidFloor)
  };
};

/**
 * 把使用者輸入的出價上限收斂成 `number | undefined`。
 *
 * 共用的理由與判定函式本身一樣重要：面板拿到的是 `<input>` 的字串、卡片拿到的是 formik 的值
 * （清空時是 `''`），若兩邊各自解析，同一個輸入會在兩頁得到不同結果 —— 實際發生過：
 * 出價 `0` 在面板是「還沒輸入」（全列未試算），在卡片卻通過成數值 0、把整欄判成「買不到量」。
 * 判定共用了但輸入的正規化沒共用，等於沒共用。
 *
 * 非數字 / 0 / 負數一律當「還沒輸入」：0 元不是一個有意義的出價上限。
 */
export const toBidCap = (value: unknown): number | undefined => {
  const numeric = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value.trim())
      : NaN;
  return isFinite(numeric) && numeric > 0 ? numeric : undefined;
};

export const getKeywordBidCompetitiveness = (
  bidCap: number | undefined,
  row: Pick<SuggestKeywords, 'bidPrice' | 'maxCpc' | 'cpcCounts'>,
  bidFloor: number
): KeywordBidCompetitiveness => {
  if (bidCap === undefined || !isFinite(bidCap)) {
    return KeywordBidCompetitiveness.NOT_EVALUATED;
  }
  // 沒有 CPC 資料的字用預設區間、經銷商下限再把兩端抬高 —— 判別與顯示共用同一份,
  // 見 resolveKeywordBidRange。判定與畫面用的是同一個區間,由構造保證。
  const { floor, cap } = resolveKeywordBidRange(row, bidFloor);
  if (bidCap > cap) {
    return KeywordBidCompetitiveness.STRONG;
  }
  if (bidCap >= floor) {
    return KeywordBidCompetitiveness.COMPETITIVE;
  }
  return KeywordBidCompetitiveness.BELOW_SUGGESTED;
};

/**
 * capsule 顏色。兩個消費端共用這一份 —— `優化目標` 的出價試算面板與 `基本資訊` 的
 * 出價效益參考卡片講的是同一個判定，只是標籤不同（競爭力強／大部分有量、可競爭／部分有量、
 * 低於建議／買不到量）。各寫一份就會漂移：實際發生過 —— 卡片那份把 `STRONG` 與
 * `COMPETITIVE` 都設成 `success`，使用者切 tab 會看到同一個關鍵字換了顏色。
 *
 * `deep-warning` 是 #ee75ee 粉紫不是琥珀，「可競爭／部分有量」要的琥珀是
 * `warning`（`$warning` #ffa953）。
 */
export const COMPETITIVENESS_STATUS_COLOR: { [key in KeywordBidCompetitiveness]: string } = {
  [KeywordBidCompetitiveness.NOT_EVALUATED]: 'light',
  [KeywordBidCompetitiveness.STRONG]: 'success',
  [KeywordBidCompetitiveness.COMPETITIVE]: 'warning',
  [KeywordBidCompetitiveness.BELOW_SUGGESTED]: 'danger'
};

/**
 * 表格排序用的名次：依「買得到的量」由少到多。
 *
 * 直接拿 enum 的字串排序會得到 belowSuggested < competitive < notEvaluated < strong
 * 這種按字母的順序，讀起來毫無意義。
 *
 * `NOT_EVALUATED` 放最後而不是夾在中間：它不是量的多寡，是「後端沒有這個字的 CPC 資料、
 * 無從判定」，插在序列中間會讓遞增排序看起來像它比 belowSuggested 好、比 competitive 差。
 *
 * 與 COMPETITIVENESS_STATUS_COLOR 同理，兩張表（出價試算面板 / 出價效益參考卡片）共用
 * 這一份，各寫一次就會漂移。
 */
export const COMPETITIVENESS_SORT_ORDER: { [key in KeywordBidCompetitiveness]: number } = {
  [KeywordBidCompetitiveness.BELOW_SUGGESTED]: 0,
  [KeywordBidCompetitiveness.COMPETITIVE]: 1,
  [KeywordBidCompetitiveness.STRONG]: 2,
  [KeywordBidCompetitiveness.NOT_EVALUATED]: 3
};

/** 「可競爭」的分子含「競爭力強」——「出價高過建議上限」也算買得到量。 */
export const isCompetitive = (competitiveness: KeywordBidCompetitiveness): boolean =>
  competitiveness === KeywordBidCompetitiveness.COMPETITIVE ||
  competitiveness === KeywordBidCompetitiveness.STRONG;

/**
 * 分母是移除後的剩餘關鍵字數，不是原始總數（`final-02` 移除一字後由 8 變 7 自證）。
 * 覆蓋率四捨五入到整數百分比（6/7 = 85.7% → 86%）。
 */
/**
 * 沒有 CPC 資料的字留在分母裡 —— 分母的定義是「移除後的剩餘關鍵字數」，
 * 使用者看得到它在表格上。把它從分母偷偷拿掉會讓覆蓋率對不上表格列數。
 *
 * 已知的失真，是決策不是疏漏（2026-09-02）：沒有 CPC 資料的字改用預設區間 8–20
 * 判定之後，它們也會進分子 ⇒ 出價 ≥ 8 時覆蓋率會趨近 100%，這塊磚的鑑別力大幅下降。
 * 取捨的另一邊是「每一列都算得出來、使用者知道要設多少」，那被判定為更重要。
 * 要恢復鑑別力就得改分母（只算有 CPC 資料的字）並在磚上標明母體，那是另一個決定 ——
 * 別在不知情的情況下單方面改掉其中一邊。
 */
export const getKeywordCoverage = (
  bidCap: number | undefined,
  rows: Pick<SuggestKeywords, 'bidPrice' | 'maxCpc' | 'cpcCounts'>[],
  bidFloor: number
): { competitiveCount: number, total: number, coverageRate: number } => {
  const total = rows.length;
  const competitiveCount =
    rows.filter(row => isCompetitive(getKeywordBidCompetitiveness(bidCap, row, bidFloor))).length;
  return {
    competitiveCount,
    total,
    coverageRate: total === 0 ? 0 : Math.round((competitiveCount / total) * 100)
  };
};
